import { useState, useRef, useEffect } from 'react';
import { useInView } from 'framer-motion';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import Title from '../../Atoms/Title';
import Text from '../../Atoms/Text';
import Button from '../../Atoms/Button';
import Image from '../../Atoms/Image';
import Tag from '../../Atoms/Tag';
import List from '../../Molecules/List';
import Accordion, {
  Props as AccordionProps
} from '../../Molecules/Accordion';
import SocialBar from '../../Molecules/SocialBar';
import VideoBox from '../../Molecules/VideoBox';
import LogoList from '../../Molecules/LogoList';
import { Props as CardVacancyProps } from '../../Molecules/CardVacancy';
import CardReviewSlider from '../../Organisms/CardReviewSlider';
import CardVacancyCarousel from '../../Organisms/CardVacancyCarousel';
import BlockEmployer from '../../Organisms/BlockEmployer';
import Rating from '../../Organisms/Rating';
import CardPersonContact from '../../Organisms/CardPersonContact';

import ApplicationForm from '../../Organisms/ApplicationForm';
import CardIconList from '../../Organisms/CardIconList';
import { logosJobDetails } from '../../../data/logosListAndImageSliderData';
import vacancyList from '../../../data/vacancyListData';

import elements from '../../../data/accordionData';
import svg from '../../../constants/svg';
import reviews from '../../../data/cardReviewSliderData';
import bgHero from '../../../assets/img/vd-header-monteurs.png';
import ratingLogo from '../../../assets/img/indeed-logo.svg';
import candidate3 from '../../../assets/img/candidate-remon-big.webp';
import ModalShareJob from '../../Organisms/ModalShareJob';

function JobDetail() {
  const [headerIsSticky, setHeaderIsSticky] =
    useState(false);

  const heroRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(heroRef, { once: false });
  const isBigScreen = useMediaQuery({ minWidth: 1024 });

  const [modalIsOpen, setModalIsOpen] =
    useState<boolean>(false);

  function handleModalState(open: boolean) {
    if (open) {
      setModalIsOpen(true);
    } else {
      setModalIsOpen(false);
    }
  }

  useEffect(() => {
    if (!isInView) {
      setHeaderIsSticky(true);
    } else {
      setHeaderIsSticky(false);
    }
  }, [isInView]);

  return (
    <>
      <div
        ref={heroRef}
        className="relative bg-sl-indigo pt-20 pb-10 text-white md:pb-16 md:pt-28 lg:min-h-[348px] lg:py-28"
        style={{
          background: `no-repeat 50%/cover #0e1c6b url(${bgHero})`
        }}
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              'linear-gradient(225deg,rgba(14,28,107,.8),rgba(95,111,255,.8) 0)'
          }}
        />
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col gap-7 px-7 @container">
          <Title color="inherit" size={3}>
            Field Service Engineer
          </Title>
          <div className="flex flex-row flex-wrap items-start gap-7 lg:flex-col lg:@5xl:flex-row">
            <Button
              text="Solliciteer direct"
              iconName="arrow-down"
              color="accent"
              size="lg"
              smallText="Binnen 1 uur reacie"
              smallTextStyle="primary"
            />
            <Button
              text="Solliciteer via Whatsapp"
              iconName="whatsapp"
              size="lg"
              color="green-wa"
              className="hidden sm:block"
              shadow
            />
            <Button
              iconName="whatsapp"
              size="lg"
              color="green-wa"
              className="block sm:hidden"
            />
          </div>

          <VideoBox
            url="https://youtu.be/-RjDhAeXrHI"
            className="absolute top-full left-7 right-7 z-10 mx-auto hidden max-w-lg @container @3xl:max-w-xl @4xl:max-w-md @6xl:max-w-lg lg:left-auto lg:block lg:w-full lg:-translate-y-1/3"
          >
            <div className="h-full w-full px-7 text-white @md:py-3 @lg:py-7">
              <div className="flex h-full w-full items-center justify-center overflow-y-auto sm:justify-start">
                <div className="grid gap-y-3 sm:max-w-[250px]">
                  <Title
                    as="h4"
                    size={5}
                    color="white"
                    weight={900}
                    className="hidden sm:block"
                  >
                    <span>
                      De beste banen
                      <br /> voor <b>Monteurs</b>
                    </span>
                  </Title>
                  <Text
                    color="inherit"
                    weight={500}
                    className="hidden sm:block"
                  >
                    <span className="text-sm lg:text-base">
                      Jij weet wat je wil en wij weten waar
                      je dat kan doen. Check de video om te
                      zien hoe wij dat doen!
                    </span>
                  </Text>
                  <span className="my-auto flex flex-row items-center gap-3 sm:mt-auto">
                    <Button
                      iconName="play"
                      fullRounded
                      color="white"
                      size="lg"
                    />
                    <Text
                      color="inherit"
                      className="hidden sm:inline-flex"
                      weight={600}
                    >
                      Speel af
                    </Text>
                  </span>
                </div>
              </div>
            </div>
          </VideoBox>
        </div>
        <div className="pointer-events-none absolute bottom-0 right-0 left-0 lg:-top-52 lg:bottom-auto xl:-top-40">
          <div className="mx-auto flex max-w-screen-2xl items-start justify-end">
            <div className="relative -mr-24 hidden lg:block xl:-mr-32">
              {svg.jobDetailsDecorHero}
            </div>
            <div className="block lg:hidden">
              {svg.jobDetailsDecorHeroMobile}
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames([
          'fixed bottom-0 z-50 w-full bg-white py-3.5 text-sl-blue shadow-2xl duration-300 lg:top-0 lg:bottom-auto lg:shadow-xl',
          headerIsSticky && 'visible opacity-100',
          !headerIsSticky && 'invisible opacity-0'
        ])}
      >
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center justify-between gap-7 px-3 lg:flex-row">
          <Title
            color="inherit"
            as="h2"
            size={4}
            className="hidden lg:block"
          >
            Field Service Engineer
          </Title>
          <ModalShareJob
            modalOpen={modalIsOpen}
            // eslint-disable-next-line react/jsx-no-bind
            modalHandler={handleModalState}
          />
          <div className="flex flex-row flex-wrap items-center gap-3.5 lg:gap-7">
            <Button
              iconName="heart"
              color="secondary-pale"
              onClick={() => handleModalState(true)}
            />
            <Button
              text="Solliciteer via Whatsapp"
              color="green-wa"
              className="hidden sm:inline-flex"
            />
            <Button
              iconName="whatsapp"
              color="green-wa"
              className="inline-flex sm:hidden"
            />
            <Button
              text="Solliciteer direct"
              color="accent"
            />
          </div>
        </div>
      </div>
      <div className="bg-white py-10">
        <div className="gap-17 mx-auto flex max-w-7xl flex-col px-7">
          <div className="flex flex-row flex-wrap gap-3.5 text-base text-sl-blue md:mb-6 md:gap-7">
            <Tag icon="euro" text="2575,00 - 3875,00" />
            <Tag icon="location" text="Zwolle" />
            <Tag icon="award" text="MBO" />
          </div>

          <div className="relative flex flex-col gap-7 lg:flex-row">
            <div className="flex-1">
              <Accordion
                elements={
                  elements as AccordionProps['elements']
                }
                scrollToActiveItem
                scrollOffset={isBigScreen ? 80 : 0}
              />

              <div className="flex flex-col gap-7 rounded-xl bg-sl-indigo p-7 text-white lg:-mb-28 lg:max-w-xl">
                <Title color="inherit" as="h2" size={3}>
                  Dit krijg je
                </Title>
                <List
                  icon="badge-checkmark"
                  listItems={[
                    'Een basissalaris tussen de € 2.575 en € 3.875 bruto per maand\n',
                    'Een jaarcontract met uitzicht op een vast dienstverband\n',
                    'Verschillende functie-gebonden toeslagen zoals een overnachtingstoeslag, weekendtoeslag en een koffervergoeding',
                    '25 vakantiedagen en 13 ADV-dagen',
                    'Uitgebreide interne opleidings- en ontwikkelingsmogelijkheden',
                    'Uitstekend secundaire arbeidsvoorwaardenpakket inclusief: laptop, telefoon en creditcard van de zaak',
                    'Een uitdagende functie als (Internationaal) Field Service Engineer met een grote mate van zelfstandigheid binnen een internationaal actieve organisatie met forse groeiambitie',
                    'Verdien €1.000,- door een medetechneut aan te dragen!'
                  ]}
                />
              </div>

              <VideoBox
                url="https://youtu.be/-RjDhAeXrHI"
                className="z-10 mx-auto mt-7 max-w-lg lg:hidden lg:w-full"
              >
                <div className="h-full w-full px-7 py-7 text-white">
                  <div className="flex h-full w-full items-center justify-center overflow-y-auto sm:justify-start">
                    <div className="grid gap-y-3 sm:max-w-[250px]">
                      <Title
                        as="h4"
                        size={5}
                        color="white"
                        weight={900}
                        className="hidden sm:block"
                      >
                        <span>
                          De beste banen
                          <br /> voor <b>Monteurs</b>
                        </span>
                      </Title>
                      <Text
                        color="inherit"
                        weight={500}
                        className="hidden sm:block"
                      >
                        <span className="text-sm lg:text-base">
                          Jij weet wat je wil en wij weten
                          waar je dat kan doen. Check de
                          video om te zien hoe wij dat doen!
                        </span>
                      </Text>
                      <span className="my-auto flex flex-row items-center gap-3 sm:mt-auto">
                        <Button
                          iconName="play"
                          fullRounded
                          color="white"
                          size="lg"
                        />
                        <Text
                          color="inherit"
                          className="hidden sm:inline-flex"
                          weight={600}
                        >
                          Speel af
                        </Text>
                      </span>
                    </div>
                  </div>
                </div>
              </VideoBox>
            </div>
            <div className="mx-auto flex w-full max-w-lg shrink-0 flex-col gap-7 lg:w-96 lg:max-w-none xl:pt-7">
              <Rating
                rating={4.37}
                info="261 reviews"
                logoSrc={ratingLogo}
              />

              <div className="sticky top-28 w-full self-start lg:w-96">
                <CardPersonContact
                  avatar={{
                    src: candidate3,
                    alt: 'Robin Jansen Consultant Monteurs'
                  }}
                  position="Consultant Monteurs"
                  title="Stel je vraag aan"
                  name="Robin Jansen"
                  pills={[
                    {
                      tag: {
                        text: 'Bel',
                        icon: 'phone'
                      },
                      link: 'tel:#!',
                      linkText: '0883231332'
                    },
                    {
                      tag: {
                        text: 'App',
                        icon: 'whatsapp'
                      },
                      link: 'tel:#!',
                      linkText: '0619734306'
                    },
                    {
                      tag: {
                        text: 'Mail',
                        icon: 'mail'
                      },
                      link: 'mailto:talent@synsel.nl',
                      linkText: 'talent@synsel.nl'
                    }
                  ]}
                  buttons={[
                    {
                      text: 'Solliciteer direct',
                      iconName: 'arrow-down',
                      color: 'accent',
                      size: 'lg',
                      fluid: true,
                      shadow: true,
                      className: '!justify-between'
                    },
                    {
                      text: 'Wil je weten of deze vacature bij je past?',
                      iconName: 'circle-info',
                      iconPosition: 'left',
                      fullRounded: true,
                      size: 'sm',
                      color: 'secondary-outline',
                      className: 'py-1 px-2'
                    }
                  ]}
                  list={[
                    <Text color="white" key={1}>
                      <span className="text-xs md:text-sm lg:text-base">
                        Binnen <b>1 uur</b> reactie
                      </span>
                    </Text>,
                    <Text color="white" key={2}>
                      <span className="text-xs md:text-sm lg:text-base">
                        Geen klik? Wij vinden de{' '}
                        <b>passende baan</b>
                      </span>
                    </Text>,
                    <Text color="white" key={3}>
                      <span className="text-xs md:text-sm lg:text-base">
                        Monteurs beoordelen ons met een{' '}
                        <b>9.3</b>
                      </span>
                    </Text>
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-sl-blue py-10 text-white lg:py-32">
        <div className="mx-auto w-full max-w-7xl px-7">
          <Title
            as="h2"
            className="mb-7 text-center lg:mb-14"
            color="inherit"
          >
            Zet jouw volgende stap
          </Title>
          <div className="grid grid-cols-1 items-start gap-7 md:grid-cols-2 lg:gap-14">
            <ApplicationForm />
            <div className="flex flex-col items-start justify-start gap-7 @container lg:py-7">
              <Title as="h2" color="inherit">
                Hulp nodig?
              </Title>
              <Text color="inherit">
                Op basis van jouw profiel kijken wij welke
                mogelijkheden er voor jou zijn. Binnen 1 uur
                nemen we contact met je op over jouw
                mogelijkheden.
              </Text>
              <div className="flex flex-row flex-wrap items-center gap-3.5">
                <Button
                  text="Stuur CV"
                  color="white-outline"
                />{' '}
                of{' '}
                <Button
                  text="Solliciteer via Whatsapp"
                  iconName="whatsapp"
                  color="green-wa"
                  shadow
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-3.5 @xl:flex-row">
                <Text
                  color="inherit"
                  className="text-center @xl:mr-auto @xl:text-left"
                >
                  <span>
                    of neem contact op met <br />
                    <b>Tim Wilbrink</b>
                  </span>
                </Text>
                <div className="flex flex-row flex-wrap gap-3.5 @xl:flex-nowrap">
                  <SocialBar
                    rounded
                    contacts
                    iconClassName="text-lg"
                    socialItems={[
                      {
                        icon: 'phone',
                        url: '#!',
                        urlType: 'tel',
                        tooltip: 'Bel: 0883231332'
                      },
                      {
                        icon: 'mail',
                        url: '#!',
                        urlType: 'mail',
                        tooltip: 'Mail: talent@synsel.nl'
                      },
                      {
                        icon: 'whatsapp',
                        url: '#!',
                        tooltip: 'App: 0619734306'
                      },
                      {
                        icon: 'comments',
                        url: '#!',
                        tooltip: 'Chatten'
                      }
                    ]}
                  />
                  <div className="relative h-20 w-20 overflow-hidden rounded-full @lg:h-24 @lg:w-24 @lg:min-w-[96px]">
                    <Image
                      src={candidate3}
                      alt="Tim Wilbrink"
                      cover
                    />
                  </div>
                </div>
              </div>
              <CardIconList
                cards={[
                  {
                    icon: 'rocket-regular',
                    title: 'De beste baan'
                  },
                  {
                    icon: 'file-contract-regular',
                    title: 'De beste voorwaarden'
                  },
                  {
                    icon: 'lock-keyhole-regular',
                    title: 'Alleen vaste banen'
                  }
                ]}
                className="mt-7"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white py-10 lg:py-16">
        <div className="mx-auto w-full max-w-7xl px-7">
          <Title as="h2" size={3}>
            Onze sollicitanten werken o.a. bij:
          </Title>
          <LogoList
            images={logosJobDetails}
            className="justify-between pt-7"
          />
        </div>
      </div>
      <div className="relative flex flex-col bg-sl-indigo pt-16 pb-10 lg:py-24">
        <div className="relative z-20 mx-auto w-full max-w-7xl px-7">
          <Title
            as="h2"
            className="text-center"
            color="white"
          >
            <span>
              1.533 tevreden kandidaten <br /> gingen je
              voor!
            </span>
          </Title>
        </div>
        <div className="pointer-events-none absolute inset-0 z-10 mx-auto w-full max-w-[1600px] select-none">
          <div className="absolute top-full left-0 hidden -translate-y-1/2 lg:block">
            {svg.qroCircleWhiteOpen}
          </div>

          <div className="absolute bottom-full right-0 hidden translate-x-1/4 translate-y-1/2 lg:block">
            {svg.lqkbCircleDark}
          </div>
        </div>
        <div className="overflow-hidden lg:mt-12">
          <CardReviewSlider reviewList={reviews} />
        </div>
      </div>
      <BlockEmployer />
      <div
        className="bg-sl-gray-light pt-16 pb-10 lg:py-28"
        style={{ backgroundColor: '#f7f8ff' }}
      >
        <div className="mx-auto flex max-w-7xl flex-col px-7">
          <Title as="h2" size={2} className="text-left">
            Gerelateerde vacatures
          </Title>
        </div>
        <div className="w-full px-7">
          <CardVacancyCarousel
            utilityButton={{
              text: 'Bekijk alles',
              color: 'secondary-outline',
              iconName: 'grid'
            }}
            vacancyList={vacancyList as CardVacancyProps[]}
          />
        </div>
      </div>
    </>
  );
}

export default JobDetail;
